import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { CourseDesignContext } from '../context/CourseDesignContext';
import { StudentGradesContext } from '../context/StudentGradesContext';
import { CourseIDContext } from '../context/CourseIDContext';
import { AuthContext } from '../context/AuthContext';
import { fetchStudentsForUser, getESTDateString } from '../utils/serverFetchAndAddUtilities';

const useGradebookExport = () => {
  const { courseDesignState } = useContext(CourseDesignContext);
  const { studentGradesState, loadingGrades } = useContext(StudentGradesContext);
  const { courseID } = useContext(CourseIDContext);
  const { username, isInstructor } = useContext(AuthContext);

  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [error, setError] = useState(null);

  const canExport = !!isInstructor;

  useEffect(() => {
    if (!courseID || !username || !canExport) {
      setStudents([]);
      setLoadingStudents(false);
      setError(null);
      return;
    }

    let isMounted = true;

    const loadStudents = async () => {
      setLoadingStudents(true);
      try {
        const studentsData = await fetchStudentsForUser(courseID, username);
        if (!isMounted) {
          return;
        }

        studentsData.sort((a, b) => {
          const lastNameA = (a.fullname || '').split(' ').pop().toLowerCase();
          const lastNameB = (b.fullname || '').split(' ').pop().toLowerCase();
          return lastNameA.localeCompare(lastNameB);
        });

        setStudents(studentsData);
        setError(null);
      } catch (err) {
        if (!isMounted) {
          return;
        }
        console.error('Error fetching students for grade export:', err);
        setError('Unable to load student roster for export.');
      } finally {
        if (isMounted) {
          setLoadingStudents(false);
        }
      }
    };

    loadStudents();

    return () => {
      isMounted = false;
    };
  }, [canExport, courseID, username]);

  const dynamicColumns = useMemo(
    () =>
      courseDesignState
        .filter((item) => item.dataType === 'evaluation')
        .sort(
          (a, b) => a.questionCourseDate.localeCompare(b.questionCourseDate)
        )
        .map((item) => ({
          id: item.dataID,
          name: item.dataText,
        })),
    [courseDesignState]
  );

  const integrateOralExamsEnabled = useMemo(
    () =>
      courseDesignState.some(
        (item) => item.dataType === 'integrateOralExams' && item.dataText === 'Yes'
      ),
    [courseDesignState]
  );

  const handleDownloadGrades = useCallback(() => {
    if (!students.length || !dynamicColumns.length) {
      return;
    }

    const headerOrder = ['Student Name', 'Username'];

    dynamicColumns.forEach((column) => {
      headerOrder.push(`${column.name} Grade`);
      headerOrder.push(`${column.name} Complete`);
      if (integrateOralExamsEnabled) {
        headerOrder.push(`${column.name} Oral Complete`);
      }
    });

    const exportData = students.map((student) => {
      const row = {
        'Student Name': student.fullname || student.fullName || '',
        Username: student.username,
      };

      const studentGrades = studentGradesState[student.username] || [];

      dynamicColumns.forEach((column) => {
        const examGrade = studentGrades.find(
          (g) => g.evaluationDataID === column.id && g.gradingType === 'examGrade'
        );

        const gradingComplete = studentGrades.find(
          (g) =>
            g.evaluationDataID === column.id &&
            g.gradingType === 'gradingComplete' &&
            g.individualGradingData === 'yes'
        );

        const oralExamComplete = studentGrades.find(
          (g) =>
            g.evaluationDataID === column.id &&
            g.gradingType === 'oralExamComplete' &&
            g.individualGradingData === 'yes'
        );

        const numericGrade = examGrade ? parseFloat(examGrade.individualGradingData) : null;

        row[`${column.name} Grade`] =
          Number.isFinite(numericGrade) && !Number.isNaN(numericGrade) ? numericGrade : '';
        row[`${column.name} Complete`] = gradingComplete ? 'Yes' : 'No';

        if (integrateOralExamsEnabled) {
          row[`${column.name} Oral Complete`] = oralExamComplete ? 'Yes' : 'No';
        }
      });

      return row;
    });

    const worksheetData = [
      headerOrder,
      ...exportData.map((row) => headerOrder.map((key) => (row[key] !== undefined ? row[key] : ''))),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Grades');

    const today = getESTDateString(Date.now());
    const fileName = `${courseID || 'course'}-grades-${today}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  }, [
    courseID,
    dynamicColumns,
    integrateOralExamsEnabled,
    studentGradesState,
    students,
  ]);

  return {
    canExport,
    loadingStudents,
    loadingGrades,
    loading: loadingStudents || loadingGrades,
    error,
    hasStudents: students.length > 0,
    hasColumns: dynamicColumns.length > 0,
    downloadGradebook: handleDownloadGrades,
  };
};

export default useGradebookExport;
